import { useState } from 'react';
import { Button, Icon } from '@vault/ui';

/**
 * Sync-conflict banner — the lightweight cousin of the "SYNC CONFLICT" card in
 * `Vault Empty & States.dc.html` (F4): surface-1 card, strong border, modal
 * shadow, display-font title. Pinned top-center so it floats over any page.
 *
 * Fully controlled: the caller decides when it shows (`open`) and owns both
 * actions — this component only renders.
 */

export interface ConflictBannerProps {
  open: boolean;
  onReload: () => void;
  onDismiss: () => void;
}

export function ConflictBanner({ open, onReload, onDismiss }: ConflictBannerProps) {
  const [closeHover, setCloseHover] = useState(false);
  if (!open) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 14,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1150,
        width: 'min(560px, calc(100vw - 32px))',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 16px',
        background: 'var(--surface-1)',
        border: '1px solid var(--line-strong)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--shadow-modal)',
        fontFamily: 'var(--font-body)',
        color: 'var(--text-1)',
      }}
    >
      <span
        aria-hidden
        style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0 }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 13.5, fontWeight: 600, letterSpacing: '-.01em' }}>
          This vault changed in another tab.
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Reload to see the latest.</span>
      </span>
      <Button variant="primary" size="sm" onClick={onReload}>
        Reload
      </Button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        onMouseEnter={() => setCloseHover(true)}
        onMouseLeave={() => setCloseHover(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          flexShrink: 0,
          background: closeHover ? 'var(--surface-2)' : 'transparent',
          border: 'none',
          borderRadius: 'var(--r-md)',
          color: 'var(--text-3)',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}
